import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
    Upload,
    FileText,
    Loader2,
    Download,
    Pause,
    Play,
    AlertCircle,
    CheckCircle,
    Trash2,
    Info as InfoIcon,
    AlertTriangle,
    Settings,
    Mail
} from 'lucide-react'
import { apiService } from '../config/api'
import api from '../config/api'
import toast from 'react-hot-toast'
import CSVExportModal from '../components/CSVExportModal'
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Select from 'react-select';
import { useNavigate, Link } from 'react-router-dom';
import SmartPagination from '../components/SmartPagination';

const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'var(--lv-control-bg, white)',
        borderColor: state.isFocused ? '#6366f1' : 'var(--lv-control-border, #374151)',
        boxShadow: state.isFocused ? '0 0 0 2px #6366f133' : 'none',
        borderRadius: '0.5rem',
        minHeight: '2.5rem',
        fontSize: '1rem',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        color: 'var(--lv-control-text, #111827)'
    }),
    valueContainer: base => ({
        ...base,
        color: 'var(--lv-control-text, #111827)'
    }),
    menu: base => ({
        ...base,
        zIndex: 9999,
        borderRadius: '0.5rem',
        fontSize: '1rem',
        backgroundColor: 'var(--lv-menu-bg, white)',
        color: 'var(--lv-menu-text, #111827)'
    }),
    menuList: base => ({
        ...base,
        backgroundColor: 'var(--lv-menu-bg, white)',
        color: 'var(--lv-menu-text, #111827)'
    }),
    menuPortal: base => ({
        ...base,
        zIndex: 9999,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--lv-option-selected, #e5e7eb)'
            : state.isFocused
                ? 'var(--lv-option-hover, #f3f4f6)'
                : 'var(--lv-option-bg, white)',
        color: 'var(--lv-option-text, #111827)'
    }),
    singleValue: base => ({
        ...base,
        color: 'var(--lv-control-text, #111827)'
    }),
    placeholder: base => ({
        ...base,
        color: 'var(--lv-placeholder, #6b7280)'
    }),
    input: base => ({
        ...base,
        color: 'var(--lv-control-text, #111827)'
    }),
    dropdownIndicator: base => ({
        ...base,
        color: 'var(--lv-indicator, #9ca3af)',
        paddingRight: '0.5rem',
    }),
    clearIndicator: base => ({
        ...base,
        color: 'var(--lv-indicator, #9ca3af)',
        paddingRight: '0.5rem',
    }),
    indicatorSeparator: base => ({
        ...base,
        backgroundColor: 'var(--lv-control-border, #374151)'
    })
};

export default function ListVerification() {
    const [uploadedFile, setUploadedFile] = useState(null)
    const [bulkResults, setBulkResults] = useState(null)
    const [isBulkVerifying, setIsBulkVerifying] = useState(false)
    const [progress, setProgress] = useState(null)
    const [jobId, setJobId] = useState(null)
    const [jobs, setJobs] = useState([])
    const [jobsTotal, setJobsTotal] = useState(0)
    const [jobSearchTerm, setJobSearchTerm] = useState('')
    const [debouncedJobSearch, setDebouncedJobSearch] = useState('')
    const [workerConcurrency, setWorkerConcurrency] = useState(2)
    const [isLoadingConcurrency, setIsLoadingConcurrency] = useState(true)
    const pollingRef = useRef(null)
    const jobsFirstFetch = useRef(true)
    const [maxUploadSizeMB, setMaxUploadSizeMB] = useState(10)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadAbortController, setUploadAbortController] = useState(null)
    const [uploadComplete, setUploadComplete] = useState(false)
    const [jobsLoading, setJobsLoading] = useState(true)

    // CSV Export modal state
    const [exportModalOpen, setExportModalOpen] = useState(false)
    const [exportScope, setExportScope] = useState('page')
    const [selectedTypes, setSelectedTypes] = useState(['ALL'])
    const [selectedJobForExport, setSelectedJobForExport] = useState(null)

    // Delete confirmation modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [jobToDelete, setJobToDelete] = useState(null)
    const [deletingJobId, setDeletingJobId] = useState(null) // Track which job is being deleted
    const [deletingJobIds, setDeletingJobIds] = useState(new Set()) // Track multiple jobs being deleted
    const [isDeletingAny, setIsDeletingAny] = useState(false) // Track if any deletion is in progress

    // Bulk delete state
    const [selectedJobs, setSelectedJobs] = useState(new Set())
    const [selectAll, setSelectAll] = useState(false)
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false)
    const selectedJobsRef = useRef(new Set()) // Ref to track current selection without causing re-renders

    // Pagination state for jobs
    const [jobPage, setJobPage] = useState(1)
    const jobsPerPage = 12 // Increased for better overview

    const totalJobPages = Math.max(1, Math.ceil((jobsTotal || 0) / jobsPerPage) || 1)

    useEffect(() => {
        const t = setTimeout(() => setDebouncedJobSearch(jobSearchTerm.trim()), 400)
        return () => clearTimeout(t)
    }, [jobSearchTerm])

    useEffect(() => {
        setJobPage(1)
    }, [debouncedJobSearch])

    // Add state for cancel confirmation at the top
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [jobToCancel, setJobToCancel] = useState(null);
    const [isCancellingJob, setIsCancellingJob] = useState(false);

    // State to track which job to highlight after pagination updates
    const [selectedJobToHighlight, setSelectedJobToHighlight] = useState(null);
    const [pendingPage, setPendingPage] = useState(null);

    // Upload modal state
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const [jobToFocusAfterRefresh, setJobToFocusAfterRefresh] = useState(null);
    const [previewItems, setPreviewItems] = useState([]);
    const [previewJobId, setPreviewJobId] = useState(null);

    const navigate = useNavigate();

    // Settings check state - initialize as not configured to prevent race condition
    const [settingsCheck, setSettingsCheck] = useState(null)
    const [settingsCheckLoading, setSettingsCheckLoading] = useState(true)
    const [onlineNodesCount, setOnlineNodesCount] = useState(0)
    const [isNodesCheckLoading, setIsNodesCheckLoading] = useState(true)

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv']
        },
        maxFiles: 1,
        onDrop: (acceptedFiles) => {
            const file = acceptedFiles[0]
            if (file && file.size > maxUploadSizeMB * 1024 * 1024) {
                toast.error(`File is too large. Max allowed size is ${maxUploadSizeMB} MB.`)
                return
            }
            setUploadedFile(file)
            setBulkResults(null)
        }
    })

    // On mount, fetch jobs, concurrency, and max upload size
    useEffect(() => {
        const loadConcurrencySettings = async () => {
            try {
                const concurrency = await apiService.getWorkerConcurrency()
                setWorkerConcurrency(concurrency)
            } catch (error) {
                // no-op
            } finally {
                setIsLoadingConcurrency(false)
            }
        }
        const loadMaxUploadSize = async () => {
            try {
                const response = await apiService.getSettings()
                const mb = response.data.data?.uploads?.max_upload_size_mb || 10
                setMaxUploadSizeMB(mb)
            } catch (error) {
                setMaxUploadSizeMB(10)
            }
        }
        const checkSettings = async () => {
            try {
                const response = await apiService.checkSettings()
                setSettingsCheck(response.data.data)
            } catch (error) {
                // no-op
            } finally {
                setSettingsCheckLoading(false)
            }
        }
        const checkOnlineNodes = async () => {
            try {
                const response = await apiService.getSatellites()
                const nodes = response.data.data || []
                // A node is "active" if it's not offline/error AND not manually paused by the user.
                // 'rate_limited' and 'maintenance' are still operational states.
                const onlineAndActive = nodes.filter(n =>
                    !n.is_paused &&
                    !['offline', 'error'].includes(n.status)
                ).length
                setOnlineNodesCount(onlineAndActive)
            } catch (error) {
                // no-op
                setOnlineNodesCount(0)
            } finally {
                setIsNodesCheckLoading(false)
            }
        }
        loadConcurrencySettings()
        loadMaxUploadSize()
        checkSettings()
        checkOnlineNodes() // call immediately on mount
        // Periodically refresh concurrency and node status every 10 seconds
        const refreshInterval = setInterval(() => {
            loadConcurrencySettings();
            checkOnlineNodes();
        }, 10000);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current)
            }
            clearInterval(refreshInterval)
        }
    }, [])

    // Lock body scroll when upload modal is open
    useEffect(() => {
        if (uploadModalOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [uploadModalOpen]);

    const handleBulkVerification = async () => {
        if (!uploadedFile) {
            toast.error('Please upload a CSV file first')
            return
        }

        // Scanner settings check is no longer needed here since the upload section is completely hidden when not configured

        try {
            setIsUploading(true)
            setUploadProgress(0)
            setUploadComplete(false)
            setIsBulkVerifying(false)
            setBulkResults(null)
            setProgress(null)
            setJobId(null)
            setJobsLoading(true)

            // Create FormData for file upload
            const formData = new FormData()
            formData.append('file', uploadedFile)

            // Create abort controller for upload cancellation
            const abortController = new AbortController()
            setUploadAbortController(abortController)

            // Upload file with progress tracking
            const response = await api.post('/verify/upload', formData, {
                timeout: 1800000, // 30 minutes
                signal: abortController.signal,
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        setUploadProgress(percentCompleted)
                    }
                }
            })

            if (response.data.success) {
                setUploadProgress(100)
                setUploadComplete(true)
                setJobId(response.data.data.jobId)
                toast.success(response.data.data.message || 'File uploaded successfully! Processing in background...')
                setUploadedFile(null)

                // Start polling for upload processing progress
                startUploadProgressPolling(response.data.data.jobId)

                // Refresh jobs list
                await refreshJobs(true)

                // Clear upload state after delay
                setTimeout(() => {
                    setUploadComplete(false)
                    setUploadProgress(0)
                    setIsUploading(false)
                    setUploadAbortController(null)
                    setUploadModalOpen(false) // Auto-close modal after upload
                    setUploadedFile(null) // Reset uploaded file
                }, 2000)
                setJobToFocusAfterRefresh(response.data.data.jobId);
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                toast('Upload cancelled')
            } else if (error.response) {
                // Handle API errors (like CSV validation errors)
                const errorMessage = error.response.data?.message || 'Upload failed'
                toast.error(errorMessage)
            } else if (error.request) {
                // Network error
                const errorMessage = 'Network error: Unable to connect to server'
                toast.error(errorMessage)
            } else {
                // Other errors
                const errorMessage = 'Upload failed: ' + (error.message || 'Unknown error')
                toast.error(errorMessage)
            }
            setIsUploading(false)
            setUploadProgress(0)
            setUploadAbortController(null)
            setUploadComplete(false)
            setJobsLoading(false)
        } finally {
            setIsBulkVerifying(false)
        }
    }

    // Poll upload processing progress
    const startUploadProgressPolling = (jobId) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await api.get(`/verify/jobs/${jobId}`)
                const job = response.data.data

                if (job.status === 'failed') {
                    clearInterval(pollInterval)
                    toast.error(`Upload failed: ${job.error_message || 'Unknown error'}`)
                    await refreshJobs(true)
                } else if (job.status === 'ready') {
                    clearInterval(pollInterval)
                    toast.success('Upload processing completed! Job is ready for verification.')
                    await refreshJobs(true)
                } else if (job.status === 'uploading') {
                    // Show progress for upload processing
                    const progressPercent = job.total_emails > 0 ? Math.round((job.processed_emails / job.total_emails) * 100) : 0
                    // Upload processing logging removed for production
                }
            } catch (error) {
                // no-op
            }
        }, 2000) // Poll every 2 seconds

        // Stop polling after 5 minutes to prevent infinite polling
        setTimeout(() => {
            clearInterval(pollInterval)
        }, 5 * 60 * 1000)
    }

    const handleCancelUpload = () => {
        if (uploadAbortController) {
            uploadAbortController.abort()
            setIsUploading(false)
            setUploadProgress(0)
            setUploadAbortController(null)
            toast('Upload cancelled')
        }
    }

    const handleCancelJob = async () => {
        try {
            await apiService.api.post('/upload/cancel')
            if (pollingRef.current) clearInterval(pollingRef.current)
            setIsBulkVerifying(false)
            setProgress((prev) => prev ? { ...prev, status: 'cancelled', error: 'Job cancelled by user.' } : null)
            toast.success('Bulk verification cancelled')
        } catch (error) {
            toast.error('Failed to cancel job')
        }
    }

    // Cancel a specific job by ID
    const handleCancelJobById = async (jobId) => {
        setJobToCancel(jobId);
        setCancelModalOpen(true);
    }

    const handlePauseJob = async (jobId) => {
        try {
            await apiService.pauseJob(jobId)
            toast.success('Job paused')
            await refreshJobs()
            setJobToFocusAfterRefresh(jobId);
        } catch (error) {
            // no-op
        }
    }

    const handleResumeJob = async (jobId) => {
        if (!canStartNewJob()) {
            toast.error('Concurrency limit reached. Please pause another job before starting or resuming.')
            return
        }
        try {
            await apiService.resumeJob(jobId)
            toast.success('Job resumed')
            await refreshJobs()
            setJobToFocusAfterRefresh(jobId);
        } catch (error) {
            // no-op
        }
    }

    const handleDeleteJob = (jobId) => {
        if (isDeletingAny) {
            toast.error('Please wait for the current deletion to complete')
            return
        }
        setJobToDelete(jobId)
        setDeleteModalOpen(true)
    }

    const confirmDeleteJob = async () => {
        if (!jobToDelete || isDeletingAny) return
        try {
            setIsDeletingAny(true)
            setDeletingJobId(jobToDelete)
            setDeleteModalOpen(false)
            setJobToDelete(null)

            // Track deletion start time for UI polling logic
            window.lastDeleteStartTime = Date.now()

            // Find the job object
            const job = jobs.find(j => j.job_id === jobToDelete)

            // If job is uploading, pause it first and wait for pause
            if (job && job.status === 'uploading') {
                toast.loading('Pausing upload before deleting...', { id: 'pauseJob' })
                try {
                    await apiService.pauseJob(jobToDelete)
                    // Poll until job is actually paused
                    const start = Date.now()
                    const TIMEOUT = 15000 // 15 seconds
                    let paused = false
                    while (Date.now() - start < TIMEOUT) {
                        await refreshJobs(true)
                        const updatedJob = jobs.find(j => j.job_id === jobToDelete)
                        if (updatedJob && updatedJob.status === 'paused') {
                            paused = true
                            break
                        }
                        await new Promise(res => setTimeout(res, 1000))
                    }
                    if (paused) {
                        toast.success('Upload paused. Deleting job...', { id: 'pauseJob' })
                    } else {
                        toast.error('Pausing upload timed out. Cannot delete job.', { id: 'pauseJob' })
                        setDeletingJobId(null)
                        setIsDeletingAny(false)
                        return
                    }
                } catch (e) {
                    // no-op
                    setDeletingJobId(null)
                    setIsDeletingAny(false)
                    return
                }
            }

            // Start deletion
            toast.loading('Deleting job...', { id: 'deleteJob' })
            await apiService.deleteJob(jobToDelete)

            // For small jobs, expect immediate completion
            const totalEmails = job?.total_emails || 0
            if (totalEmails < 1000) {
                toast.success('Job deleted successfully', { id: 'deleteJob' })
                setDeletingJobId(null)
                setIsDeletingAny(false)
                await refreshJobs(true)
                return
            }

            // For large jobs, start polling with longer intervals
            // Immediately mark job as deleting in UI
            setJobs(prev => prev.map(j => j.job_id === jobToDelete ? { ...j, status: 'deleting' } : j))
            setDeletingJobIds(prev => new Set(prev).add(jobToDelete))

            // Optimized polling with progressive intervals and better timeout handling
            const startTime = Date.now()
            const MAX_TIMEOUT = 120000 // 2 minutes for large jobs
            let pollInterval = 5000 // Start with 5 seconds for large jobs
            let consecutiveNotFound = 0

            const pollDeletion = async () => {
                try {
                    await refreshJobs(false) // Don't show loading spinner during polling
                    const currentJob = jobs.find(j => j.job_id === jobToDelete)

                    if (!currentJob) {
                        consecutiveNotFound++
                        // Confirm deletion with multiple checks to prevent false positives
                        if (consecutiveNotFound >= 2) {
                            toast.success('Job deleted successfully', { id: 'deleteJob' })
                            setDeletingJobId(null)
                            setIsDeletingAny(false)
                            setDeletingJobIds(prev => {
                                const newSet = new Set(prev)
                                newSet.delete(jobToDelete)
                                return newSet
                            })
                            return
                        }
                    } else {
                        consecutiveNotFound = 0
                        // Check if job is in 'deleting' status or shows progress in error_message
                        if (currentJob.status === 'deleting' || (currentJob.error_message && currentJob.error_message.includes('Deleting:'))) {
                            toast.loading(`${currentJob.error_message || 'Deleting job...'}`, { id: 'deleteJob' })
                        }
                    }

                    // Check timeout
                    if (Date.now() - startTime > MAX_TIMEOUT) {
                        toast.error('Job deletion is taking longer than expected. Please check back later.', { id: 'deleteJob' })
                        setDeletingJobId(null)
                        setIsDeletingAny(false)
                        setDeletingJobIds(prev => {
                            const newSet = new Set(prev)
                            newSet.delete(jobToDelete)
                            return newSet
                        })
                        return
                    }

                    // Progressive polling intervals
                    if (Date.now() - startTime > 30000) {
                        pollInterval = 10000 // Slow down to 10 seconds after 30 seconds
                    } else if (Date.now() - startTime > 10000) {
                        pollInterval = 7000 // Slow down to 7 seconds after 10 seconds
                    }

                    setTimeout(pollDeletion, pollInterval)
                } catch (error) {
                    // no-op
                    // On error, wait longer before next poll
                    setTimeout(pollDeletion, Math.min(pollInterval * 2, 15000))
                }
            }

            // Start polling after a brief delay
            setTimeout(pollDeletion, pollInterval)

        } catch (error) {
            // no-op
            toast.error('Failed to delete job', { id: 'deleteJob' })
            setDeletingJobId(null)
            setIsDeletingAny(false)
        }
    }

    // Bulk delete functions
    const handleBulkDelete = () => {
        if (selectedJobs.size === 0) {
            toast.error('Please select jobs to delete')
            return
        }
        if (isDeletingAny) {
            toast.error('Please wait for the current deletion to complete')
            return
        }
        setBulkDeleteModalOpen(true)
    }

    const confirmBulkDelete = async () => {
        if (selectedJobs.size === 0 || isDeletingAny) return
        try {
            setIsDeletingAny(true)
            setDeletingJobIds(new Set(selectedJobs))
            setBulkDeleteModalOpen(false)

            const jobIdsArray = Array.from(selectedJobs)
            const totalJobs = jobIdsArray.length
            toast.loading(`Deleting ${totalJobs} jobs...`, { id: 'bulkDelete' })

            // Delete jobs in parallel batches to speed up process
            const batchSize = 3 // Process 3 jobs at a time
            let successCount = 0
            let failCount = 0

            for (let i = 0; i < jobIdsArray.length; i += batchSize) {
                const batch = jobIdsArray.slice(i, i + batchSize)
                const promises = batch.map(async (jobId) => {
                    try {
                        await apiService.deleteJob(jobId)
                        return { success: true, jobId }
                    } catch (error) {
                        // no-op
                        return { success: false, jobId, error }
                    }
                })

                const results = await Promise.all(promises)
                results.forEach(result => {
                    if (result.success) {
                        successCount++
                    } else {
                        failCount++
                    }
                })

                // Update progress toast
                const completed = successCount + failCount
                if (completed < totalJobs) {
                    toast.loading(`Deleting jobs... ${completed}/${totalJobs}`, { id: 'bulkDelete' })
                }
            }

            // Clear selection immediately since we've submitted all delete requests
            const newSelected = new Set()
            setSelectedJobs(newSelected)
            selectedJobsRef.current = newSelected
            setSelectAll(false)

            // Poll for completion with shorter, more frequent checks for better UX
            const start = Date.now()
            const TIMEOUT = 60000 // 60 seconds for bulk operations
            let pollCount = 0
            const MAX_POLLS = 30 // Maximum number of polling attempts

            const checkDeletion = async () => {
                pollCount++
                try {
                    await refreshJobs(false) // Don't show loading spinner during polling
                    const stillExisting = jobs.filter(j => jobIdsArray.includes(j.job_id))

                    if (stillExisting.length === 0) {
                        // All jobs deleted successfully
                        toast.success(`Successfully deleted ${successCount} jobs${failCount > 0 ? `, ${failCount} failed` : ''}`, { id: 'bulkDelete' })
                        return
                    }

                    // Check timeout conditions
                    if (Date.now() - start > TIMEOUT || pollCount >= MAX_POLLS) {
                        const remainingCount = stillExisting.length
                        toast.error(`Deletion timeout. ${successCount} deleted, ${remainingCount} remaining, ${failCount} failed. Please refresh.`, { id: 'bulkDelete' })
                        return
                    }

                    // Continue polling with shorter intervals for better feedback
                    setTimeout(checkDeletion, 2000) // Check every 2 seconds
                } catch (error) {
                    // no-op
                    // On error, retry with longer delay
                    if (pollCount < MAX_POLLS && Date.now() - start < TIMEOUT) {
                        setTimeout(checkDeletion, 5000)
                    } else {
                        toast.error('Error checking deletion status. Please refresh the page.', { id: 'bulkDelete' })
                    }
                }
            }

            // Start polling after a brief delay to allow the delete requests to process
            setTimeout(checkDeletion, 3000)

        } catch (error) {
            // no-op
            toast.error('Bulk delete failed', { id: 'bulkDelete' })
        } finally {
            setDeletingJobIds(new Set())
            setIsDeletingAny(false)
        }
    }

    const handleSelectJob = (jobId) => {
        const newSelected = new Set(selectedJobs)
        if (newSelected.has(jobId)) {
            newSelected.delete(jobId)
        } else {
            newSelected.add(jobId)
        }
        setSelectedJobs(newSelected)
        selectedJobsRef.current = newSelected
        setSelectAll(newSelected.size === jobs.length)
    }

    const handleSelectAll = () => {
        if (selectAll) {
            const newSelected = new Set()
            setSelectedJobs(newSelected)
            selectedJobsRef.current = newSelected
            setSelectAll(false)
        } else {
            const newSelected = new Set(jobs.map(j => j.job_id))
            setSelectedJobs(newSelected)
            selectedJobsRef.current = newSelected
            setSelectAll(true)
        }
    }

    const handleDownloadJob = (job) => {
        setSelectedJobForExport(job)
        setExportModalOpen(true)
        setJobToFocusAfterRefresh(job.job_id);
    }

    const handleExportDownload = async ({ exportScope, selectedTypes }) => {
        if (!selectedJobForExport) return

        try {
            // Fetch job items based on export scope
            let jobItems = []

            if (exportScope === 'page') {
                // Get first page of items
                const response = await api.get(`/verify/jobs/${selectedJobForExport.job_id}/items?limit=50&offset=0`)
                jobItems = response.data.data.items || []
            } else if (exportScope === 'all') {
                // Get all items for this job
                const response = await api.get(`/verify/jobs/${selectedJobForExport.job_id}/items?limit=10000&offset=0`)
                jobItems = response.data.data.items || []
            } else if (exportScope === 'custom') {
                // Get all items and filter by selected types
                const response = await api.get(`/verify/jobs/${selectedJobForExport.job_id}/items?limit=10000&offset=0`)
                let allItems = response.data.data.items || []

                if (!selectedTypes.includes('ALL')) {
                    allItems = allItems.filter(item =>
                        selectedTypes.includes(item.status) || selectedTypes.includes(item.type)
                    )
                }
                jobItems = allItems
            }

            // Generate CSV content
            const allColumns = new Set()
            jobItems.forEach(item => {
                let extraColumns = null
                if (item.extra_columns && typeof item.extra_columns === 'string') {
                    try {
                        extraColumns = JSON.parse(item.extra_columns)
                    } catch (e) {
                        extraColumns = null
                    }
                } else if (item.extra_columns && typeof item.extra_columns === 'object') {
                    extraColumns = item.extra_columns
                }
                if (extraColumns) {
                    Object.keys(extraColumns).forEach(key => {
                        if (key !== 'tags') {
                            allColumns.add(key)
                        }
                    })
                }
            })

            const originalColumns = Array.from(allColumns).sort()
            const verificationColumns = ['Status', 'Email Type', 'Safe to Send']
            const headers = ['email', ...originalColumns, ...verificationColumns]

            const csvContent = [
                headers.join(','),
                ...jobItems.map(item => {
                    let extraColumns = null
                    if (item.extra_columns && typeof item.extra_columns === 'string') {
                        try {
                            extraColumns = JSON.parse(item.extra_columns)
                        } catch (e) {
                            extraColumns = null
                        }
                    } else if (item.extra_columns && typeof item.extra_columns === 'object') {
                        extraColumns = item.extra_columns
                    }

                    const row = [item.email]

                    originalColumns.forEach(column => {
                        const value = extraColumns && extraColumns[column] ? extraColumns[column] : ''
                        const escapedValue = typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))
                            ? `"${value.replace(/"/g, '""')}"`
                            : value
                        row.push(escapedValue)
                    })

                    row.push(
                        item.status,
                        item.type || '',
                        item.status === 'Valid' ? 'Yes' : (item.status === 'Catch All' ? 'Risky' : 'No')
                    )

                    return row.join(',')
                })
            ].join('\n')

            // Download the CSV
            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${selectedJobForExport.job_name}_export_${new Date().toISOString().split('T')[0]}.csv`
            a.click()
            window.URL.revokeObjectURL(url)

            setExportModalOpen(false)
            setSelectedJobForExport(null)
            toast.success('Export completed successfully')
        } catch (error) {
            // no-op
            toast.error('Failed to export job data')
        }
    }

    const handleStartJob = async (jobId) => {
        if (!canStartNewJob()) {
            toast.error('Concurrency limit reached. Please pause another job before starting or resuming.')
            return
        }
        setJobPage(1)
        setPendingPage(1)
        setSelectedJobToHighlight(jobId)
        try {
            // Call the API to start the job
            await apiService.startJob(jobId)
            // Start polling for this job
            setJobId(jobId)
            setIsBulkVerifying(true)
            if (pollingRef.current) clearInterval(pollingRef.current)
            pollingRef.current = setInterval(() => pollProgress(jobId), 2000)
            toast.success('Verification started!')
            // Refresh jobs to update status
            await refreshJobs()
            setJobToFocusAfterRefresh(jobId);
        } catch (error) {
            // no-op
            toast.error('Failed to start verification')
        }
    }

    const loadJobsFromApi = useCallback(async (
        page,
        searchStr,
        showLoading = false,
        preserveSelection = true,
        queryParams = null
    ) => {
        if (showLoading) setJobsLoading(true)
        try {
            let response
            if (queryParams && typeof queryParams === 'object' && Object.keys(queryParams).length > 0) {
                response = await apiService.getJobs(queryParams)
            } else {
                response = await apiService.getJobs({
                    limit: jobsPerPage,
                    offset: (page - 1) * jobsPerPage,
                    ...(searchStr ? { search: searchStr } : {}),
                })
            }

            const responseData = response.data.data || {}
            let newJobs

            if (Array.isArray(responseData)) {
                newJobs = responseData
                setJobsTotal(newJobs.length)
            } else if (responseData.jobs && Array.isArray(responseData.jobs)) {
                newJobs = responseData.jobs
                setJobsTotal(Number(responseData.pagination?.total ?? newJobs.length))
            } else {
                newJobs = []
                setJobsTotal(0)
            }

            setJobs(newJobs)

            if (preserveSelection) {
                const existingJobIds = new Set(newJobs.map(j => j.job_id))
                const updatedSelectedJobs = new Set(Array.from(selectedJobsRef.current).filter(id => existingJobIds.has(id)))
                setSelectedJobs(updatedSelectedJobs)
                selectedJobsRef.current = updatedSelectedJobs
                setSelectAll(updatedSelectedJobs.size === newJobs.length && newJobs.length > 0)
            }
        } catch (error) {
            setJobs([])
            setJobsTotal(0)
        } finally {
            if (showLoading) setTimeout(() => setJobsLoading(false), 500)
        }
    }, [jobsPerPage])

    const refreshJobs = async (showLoading = false, preserveSelection = true, queryParams = {}) => {
        await loadJobsFromApi(jobPage, debouncedJobSearch, showLoading, preserveSelection, queryParams)
    }

    useEffect(() => {
        const showSpinner = jobsFirstFetch.current && jobPage === 1 && !debouncedJobSearch
        jobsFirstFetch.current = false
        loadJobsFromApi(jobPage, debouncedJobSearch, showSpinner)
    }, [jobPage, debouncedJobSearch, loadJobsFromApi])

    // Check if we can start a new job based on concurrency limits
    const canStartNewJob = () => {
        if (isLoadingConcurrency) return false
        // Satefy: Don't count "Processing" jobs that are actually 100% finished
        const activeJobs = jobs.filter(job =>
            job.status === 'processing' &&
            job.total_emails > 0 &&
            job.processed_emails < job.total_emails
        ).length
        return activeJobs < workerConcurrency
    }

    // Poll job progress with robust error handling
    const pollProgress = async (jobId) => {
        try {
            const response = await apiService.api.get(`/upload/progress/${jobId}`)
            setProgress(response.data.data)
            if (
                response.data.data.status === 'completed' ||
                response.data.data.status === 'error' ||
                response.data.data.status === 'cancelled' ||
                response.data.data.status === 'paused' // stop polling if paused
            ) {
                if (pollingRef.current) {
                    clearInterval(pollingRef.current)
                    pollingRef.current = null
                }
                setIsBulkVerifying(false)
                setJobId(null)
                if (response.data.data.status === 'completed') {
                    setBulkResults({
                        total: response.data.data.total,
                        results: response.data.data.results || []
                    })
                }
                setProgress(null)
                return;
            }
        } catch (error) {
            // no-op
        }
    }

    const exportResults = async (format = 'csv') => {
        try {
            const response = await apiService.exportResults(null, format)
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `verification-results-${Date.now()}.${format}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            toast.success(`Results exported as ${format.toUpperCase()}`)
        } catch (error) {
            // no-op
            toast.error('Failed to export results')
        }
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'valid':
                return <span className="text-success-500">✓</span>
            case 'invalid':
                return <span className="text-danger-500">✗</span>
            case 'disposable':
            case 'catch-all':
                return <span className="text-warning-500">!</span>
            case 'role-based':
                return <span className="text-primary-500">@</span>
            case 'uploading':
                return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            case 'processing':
                return <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
            case 'ready':
                return <span className="text-green-500">Ready</span>
            case 'pending':
                return <span className="text-gray-500">Pending</span>
            case 'completed':
                return <CheckCircle className="h-4 w-4 text-green-500" />
            case 'failed':
                return <AlertCircle className="h-4 w-4 text-red-500" />
            case 'cancelled':
                return <span className="text-gray-500">Cancelled</span>
            case 'paused':
                return <Pause className="h-4 w-4 text-yellow-500" />
            default:
                return <span className="text-gray-500">Unknown</span>
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'valid':
                return 'status-valid'
            case 'invalid':
                return 'status-invalid'
            case 'disposable':
            case 'catch-all':
                return 'status-disposable'
            case 'role-based':
                return 'status-role-based'
            default:
                return 'status-unknown'
        }
    }

    const getStatusText = (job) => {
        // Use both upload_status and status (verification_status)
        if (job.upload_status === 'pending') return 'Queued for Upload';
        if (job.upload_status === 'processing') return 'Uploading...';
        if (job.upload_status === 'failed') return 'Upload Failed';
        if (job.upload_status === 'completed' && (job.status === 'pending' || job.status === 'ready')) return 'Ready to Verify';
        if (job.status === 'uploading') return 'Processing Upload...';
        if (job.status === 'uploading_paused') return 'Upload Paused';

        // Custom override: If processing but 100% done, show as Completed
        if (job.status === 'processing' && job.total_emails > 0 && job.processed_emails >= job.total_emails) {
            return 'Completed';
        }

        if (job.status === 'processing') return 'Processing';
        if (job.status === 'completed') return 'Completed';
        if (job.status === 'failed') return 'Failed';
        if (job.status === 'cancelled') return 'Cancelled';
        if (job.status === 'paused') return 'Paused';
        if (job.status === 'ready') return 'Ready to Verify';
        if (job.status === 'pending') return 'Ready to Verify';
        return job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : '';
    };

    // Use DB-driven liveStats
    const liveStats = progress && progress.liveStats ? progress.liveStats : {}
    const verifiedPercent = progress && progress.total ? Math.round((progress.processed / progress.total) * 100) : 0

    // Helper: Should show progress bar and percent done
    const shouldShowProgress = (job) => {
        // Only show progress bar if job is processing, ready, or paused (not just pending after upload)
        return ['processing', 'ready', 'paused'].includes(job.status);
    };

    // Helper: Should show 100% done badge
    const shouldShow100PercentDone = (job) => {
        // Only show 100% done if job is completed, or processing and processed_emails === total_emails
        if (job.status === 'completed') return true;
        if (job.status === 'processing' && job.processed_emails === job.total_emails) return true;
        return false;
    };

    // Helper to get live or summary stats for a job
    const getJobStats = (job) => {
        // If this job is currently being processed and matches progress, use liveStats
        if (progress && job.job_id === jobId && isBulkVerifying && progress.liveStats) {
            return {
                valid: progress.liveStats['Valid'] || 0,
                invalid: progress.liveStats['Invalid'] || 0,
                catch_all: progress.liveStats['Catch All'] || 0,
                unknown: progress.liveStats['Unknown'] || 0,
                skipped: progress.liveStats['Skipped'] || 0,
                syntax_error: progress.liveStats['Syntax Error'] || 0,
                disposable: progress.liveStats['Disposable'] || 0,
                free_account: progress.liveStats['Free Account'] || 0,
                role_account: progress.liveStats['Role-Based'] || 0,
                processed: progress.processed || 0,
                total: progress.total || job.total_emails || 0
            };
        }
        // Otherwise, use job summary fields (corrected field names)
        return {
            valid: job.valid_count || 0,
            invalid: job.invalid_count || 0,
            catch_all: job.catch_all_count || 0,
            unknown: job.unknown_count || 0,
            skipped: job.skipped_count || 0,
            syntax_error: job.syntax_error_count || 0,
            disposable: job.disposable_count || 0,
            free_account: job.free_account_count || 0,
            role_account: job.role_account_count || 0,
            processed:
                (job.valid_count || 0) +
                (job.invalid_count || 0) +
                (job.catch_all_count || 0) +
                (job.unknown_count || 0) +
                (job.skipped_count || 0) +
                (job.syntax_error_count || 0),
            total: job.total_emails || 0
        };
    };

    // useEffect cleanup: always clear polling interval on unmount or jobId change
    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
            }
        }
    }, [jobId])



    // Poll for live stats during processing
    useEffect(() => {
        if (!jobId || !isBulkVerifying) return;

        // Dynamic polling frequency based on job size
        const getPollingFrequency = (totalEmails) => {
            if (totalEmails > 100000) return 10000; // 10 seconds for very large jobs
            if (totalEmails > 50000) return 5000;   // 5 seconds for large jobs
            return 2000; // 2 seconds for smaller jobs
        };

        const pollStats = async () => {
            try {
                const response = await apiService.api.get(`/upload/progress/${jobId}`);
                const progressData = response.data.data;
                setProgress(progressData);

                // Update jobs list with live stats
                setJobs(prevJobs => prevJobs.map(j =>
                    j.job_id === jobId
                        ? {
                            ...j,
                            status: progressData.status,
                            processed_emails: progressData.processed,
                            valid_count: progressData.liveStats?.Valid || 0,
                            invalid_count: progressData.liveStats?.Invalid || 0,
                            catch_all_count: progressData.liveStats?.['Catch All'] || 0,
                            unknown_count: progressData.liveStats?.Unknown || 0,
                            skipped_count: progressData.liveStats?.Skipped || 0,
                            syntax_error_count: progressData.liveStats?.['Syntax Error'] || 0,
                            disposable_count: progressData.liveStats?.Disposable || 0,
                            free_account_count: progressData.liveStats?.['Free Account'] || 0,
                            role_account_count: progressData.liveStats?.['Role-Based'] || 0,
                        }
                        : j
                ));

                // Fetch latest 10 items for live preview
                if (progressData.processed > 0) {
                    try {
                        const itemsRes = await api.get(`/verify/jobs/${jobId}/items?limit=10&offset=0`);
                        if (itemsRes.data && itemsRes.data.success) {
                            setPreviewItems(itemsRes.data.data.items || []);
                            setPreviewJobId(jobId);
                        }
                    } catch (err) { }
                }

                // Check for completion or termination
                const isFinished = ['completed', 'failed', 'cancelled'].includes(progressData.status);
                if (isFinished) {
                    clearInterval(interval);
                    setIsBulkVerifying(false);
                    refreshJobs(false);
                    return;
                }

                // Frequency adjustment
                const newFreq = getPollingFrequency(progressData.total || 0);
                if (currentFrequency !== newFreq) {
                    clearInterval(interval);
                    currentFrequency = newFreq;
                    interval = setInterval(pollStats, newFreq);
                }
            } catch (error) {
                if (error.response?.status === 404) {
                    clearInterval(interval);
                    setIsBulkVerifying(false);
                }
            }
        };

        let currentFrequency = 2000;
        let interval = setInterval(pollStats, currentFrequency);
        return () => clearInterval(interval);
    }, [jobId, isBulkVerifying, jobs]);

    // useEffect to update jobPage when pendingPage changes
    useEffect(() => {
        if (pendingPage !== null && pendingPage !== jobPage) {
            setJobPage(pendingPage);
        }
    }, [pendingPage, jobPage]);

    // useLayoutEffect to scroll/highlight after pagination updates
    useLayoutEffect(() => {
        if (selectedJobToHighlight) {
            let tries = 0;
            const maxTries = 10;
            function tryScroll() {
                const el = document.getElementById(`job-card-${selectedJobToHighlight}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('ring-2', 'ring-indigo-400');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-400'), 2000);
                    setSelectedJobToHighlight(null);
                    setPendingPage(null);
                } else if (tries < maxTries) {
                    tries++;
                    window.requestAnimationFrame(tryScroll);
                } else {
                    setSelectedJobToHighlight(null);
                    setPendingPage(null);
                }
            }
            tryScroll();
        }
    }, [jobPage, jobs, selectedJobToHighlight]);

    const displayJobs = jobs.filter(j => !deletingJobIds.has(j.job_id) && j.status !== 'deleting');

    // Add confirmCancelJob function
    const confirmCancelJob = async () => {
        if (!jobToCancel) return;
        setIsCancellingJob(true);
        const toastId = toast.loading('Cancelling job...');
        try {
            await apiService.cancelJob(jobToCancel);
            toast.success('Job cancelled');
            setCancelModalOpen(false);
            setJobToCancel(null);
            await refreshJobs();
        } catch (error) {
            // If the job is already cancelled (404) or not cancellable (400), treat as success
            if (error?.response?.status === 404 || error?.response?.status === 400) {
                toast.success('Job already cancelled');
                setCancelModalOpen(false);
                setJobToCancel(null);
                await refreshJobs();
            } else {
                toast.error('Failed to cancel job');
            }
        } finally {
            toast.dismiss(toastId);
            setIsCancellingJob(false);
        }
    };

    // useEffect to focus and highlight job after jobs update
    useEffect(() => {
        if (jobToFocusAfterRefresh && jobs.length > 0) {
            const newSortedJobs = [...jobs].sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });
            const newIdx = newSortedJobs.findIndex(j => j.job_id === jobToFocusAfterRefresh);
            if (newIdx !== -1) {
                const newPage = Math.floor(newIdx / jobsPerPage) + 1;
                setPendingPage(newPage);
                setSelectedJobToHighlight(jobToFocusAfterRefresh);
            }
            setJobToFocusAfterRefresh(null);
        }
    }, [jobs, jobToFocusAfterRefresh]);

    // Periodically refresh jobs list with smart polling during deletions
    useEffect(() => {
        let refreshInterval;

        const startPolling = () => {
            // Use less frequent polling during deletions but don't skip completely
            const interval = isDeletingAny ? 12000 : 8000; // 12s during deletions, 8s normally

            refreshInterval = setInterval(() => {
                // Only skip refresh during single job deletion for first 5 seconds
                if (isDeletingAny && deletingJobId && deletingJobIds.size <= 1) {
                    const now = Date.now();
                    if (window.lastDeleteStartTime && (now - window.lastDeleteStartTime) < 5000) {
                        // Skipping refresh logging removed for production
                        return;
                    }
                }

                // Refresh jobs list
                refreshJobs(false, true);
            }, interval);
        };

        startPolling();

        // Restart polling when deletion state changes
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [isDeletingAny, deletingJobIds.size]); // Restart polling when deletion state changes

    // Optimized effect for cleaning up deletingJobIds
    useEffect(() => {
        if (deletingJobIds.size > 0) {
            setDeletingJobIds(prev => {
                const newSet = new Set();
                // Only keep IDs that still exist in jobs list or are still being deleted
                prev.forEach(jobId => {
                    const jobExists = jobs.some(j => j.job_id === jobId);
                    const isActivelyDeleting = isDeletingAny && (deletingJobId === jobId);
                    if (jobExists || isActivelyDeleting) {
                        newSet.add(jobId);
                    }
                });

                // Only update if the set actually changed
                if (newSet.size !== prev.size || [...newSet].some(id => !prev.has(id))) {
                    return newSet;
                }
                return prev;
            });
        }
    }, [jobs, isDeletingAny, deletingJobId]);

    return (
        <div className="space-y-8 pb-12">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">List Verification</h1>
                <p className="mt-1 text-slate-500 dark:text-slate-400 font-medium">
                    Upload and verify email lists in bulk with enterprise-grade accuracy
                </p>
            </div>

            {/* Dark mode CSS variables for react-select */}
            <style>{`
        html.dark { 
          --lv-control-bg: #111827; 
          --lv-control-border: #374151; 
          --lv-control-text: #e5e7eb; 
          --lv-placeholder: #9ca3af; 
          --lv-menu-bg: #111827; 
          --lv-menu-text: #e5e7eb; 
          --lv-option-bg: #111827; 
          --lv-option-hover: #1f2937; 
          --lv-option-selected: #374151; 
          --lv-option-text: #e5e7eb; 
          --lv-indicator: #9ca3af; 
        }
      `}</style>

            {/* Settings warning */}
            {!settingsCheckLoading && !settingsCheck?.scannerConfigured && (
                <div className="glass border-l-4 border-warning-400 dark:border-warning-500 p-6 rounded-2xl animate-slide-up">
                    <div className="flex items-start gap-4">
                        <div className="h-12 w-12 bg-warning-100 dark:bg-warning-900/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Configuration Required</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                                Email scanner settings are not configured. Please configure your SMTP and IMAP settings to enable bulk verification.
                            </p>
                            <Link to="/settings" className="btn btn-secondary shadow-sm"><Settings className="h-4 w-4 mr-2" />Configure Settings</Link>
                        </div>
                    </div>
                </div>
            )}

            {/* No Active Nodes Warning */}
            {!isNodesCheckLoading && settingsCheck?.scannerConfigured && onlineNodesCount === 0 && (
                <div className="glass border-l-4 border-rose-400 dark:border-rose-500 p-6 rounded-2xl animate-slide-up">
                    <div className="flex items-start gap-4">
                        <div className="h-12 w-12 bg-rose-100 dark:bg-rose-900/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <AlertCircle className="h-6 w-6 text-rose-600 dark:text-rose-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">No Active Processing Nodes</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                                All verification nodes are currently offline or paused. Verification jobs will stay in "pending" status until at least one node is active.
                            </p>
                            <div className="flex gap-3">
                                <Link to="/settings" className="btn btn-secondary shadow-sm">
                                    <Settings className="h-4 w-4 mr-2" />
                                    Activate Nodes
                                </Link>
                                <a href="https://docs.softlom.com/satellite-node" target="_blank" rel="noreferrer" className="btn btn-ghost text-sm">
                                    <InfoIcon className="h-4 w-4 mr-2" />
                                    How to start a Satellite?
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Upload Modal */}
            {uploadModalOpen && settingsCheck?.scannerConfigured && (
                <div className="modal-overlay" onClick={() => setUploadModalOpen(false)}>
                    <div className="modal max-w-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                                <div className="h-12 w-12 bg-brand-100 dark:bg-brand-900/30 rounded-2xl flex items-center justify-center">
                                    <Upload className="h-6 w-6 text-brand-600" />
                                </div>
                                Import New List
                            </h3>
                            <button
                                onClick={() => setUploadModalOpen(false)}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Upload a CSV file to verify email addresses in bulk. Maximum file size: <span className="text-brand-600 font-bold">{maxUploadSizeMB}MB</span></p>

                        <div
                            {...getRootProps()}
                            className={`relative border-2 border-dashed rounded-[2rem] p-10 text-center transition-all duration-300 group ${isDragActive
                                ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-500/5'
                                : 'border-slate-200 dark:border-slate-800 hover:border-brand-400 hover:bg-slate-50/50 dark:hover:bg-slate-800/50'
                                } cursor-pointer`}
                        >
                            <input {...getInputProps()} />
                            <div className="h-16 w-16 bg-white dark:bg-slate-800 rounded-2xl shadow-premium flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                <Upload className="h-8 w-8 text-brand-500" />
                            </div>
                            <p className="text-slate-900 dark:text-white font-bold text-lg">
                                {isDragActive ? 'Drop your file here' : 'Click to select or drag & drop'}
                            </p>
                            <p className="mt-2 text-slate-500 dark:text-slate-400 text-sm font-medium">
                                Supports CSV files only
                            </p>
                        </div>

                        {uploadedFile && (
                            <div className="mt-8 p-6 glass rounded-2xl border-brand-200 dark:border-brand-800 animate-slide-up flex items-center gap-4 min-w-0">
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="h-12 w-12 bg-brand-100 dark:bg-brand-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FileText className="h-6 w-6 text-brand-600" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p
                                            className="text-slate-900 dark:text-white font-bold truncate"
                                            title={uploadedFile.name}
                                        >
                                            {uploadedFile.name}
                                        </p>
                                        <p className="text-slate-400 text-xs font-semibold">Ready for processing</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <button onClick={() => setUploadedFile(null)} className="text-slate-400 hover:text-danger-500 font-bold text-sm transition-colors whitespace-nowrap">Discard</button>
                                    <button
                                        onClick={handleBulkVerification}
                                        disabled={isUploading}
                                        className="btn btn-primary shadow-brand-500/20 whitespace-nowrap"
                                    >
                                        {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                                        {isUploading ? 'Uploading...' : 'Start Import'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {isUploading && (
                            <div className="mt-8 space-y-4 animate-fade-in">
                                <div className="flex items-center justify-between text-sm font-bold">
                                    <span className="text-slate-600 dark:text-slate-400">Uploading List Content...</span>
                                    <span className="text-brand-600">{uploadProgress}%</span>
                                </div>
                                <div className="h-3 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden shadow-inner border border-slate-200/50 dark:border-slate-800/50">
                                    <div
                                        className="h-full bg-gradient-to-r from-brand-600 to-primary-600 transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                                <div className="flex justify-center">
                                    <button onClick={handleCancelUpload} className="text-danger-500 text-xs font-bold hover:underline">Cancel Upload</button>
                                </div>
                                {uploadComplete && !isUploading && (
                                    <div className="text-center text-success-600 font-bold text-sm">Upload complete! Processing...</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Only show upload section if scanner settings are configured */}
            {/* Show spinner in upload area if settingsCheckLoading is true */}
            {settingsCheckLoading ? (
                <div className="card">
                    <div className="card-header">
                        <h3 className="text-lg font-medium text-gray-900">Bulk Email Verification</h3>
                    </div>
                    <div className="card-body flex justify-center items-center min-h-[120px]">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                        <span className="ml-3 text-blue-700 font-medium">Loading settings...</span>
                    </div>
                </div>
            ) : null}

            {/* Jobs List */}
            <div className="card">
                <div className="card-header">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setUploadModalOpen(true)}
                                disabled={settingsCheckLoading || !settingsCheck?.scannerConfigured}
                                className="btn btn-primary shadow-brand-500/20"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Upload List
                            </button>
                            <h3 className="text-base font-medium text-gray-900">All Jobs</h3>
                            {selectedJobs.size > 0 && (
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-600">
                                        {selectedJobs.size} selected
                                    </span>
                                    <button
                                        onClick={handleBulkDelete}
                                        disabled={isDeletingAny}
                                        className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md transition-colors ${isDeletingAny
                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                            }`}
                                    >
                                        {isDeletingAny ? (
                                            <>
                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                Deleting...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 className="h-3 w-3 mr-1" />
                                                Delete Selected
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        {!isLoadingConcurrency && (
                            <div className="flex items-center space-x-2 text-xs">
                                <span className="text-gray-600">Concurrency:</span>
                                <span className={`font-medium ${jobs.filter(j =>
                                    j.status === 'processing' &&
                                    j.total_emails > 0 &&
                                    j.processed_emails < j.total_emails
                                ).length >= workerConcurrency
                                    ? 'text-orange-600'
                                    : 'text-green-600'
                                    }`}>
                                    {jobs.filter(j =>
                                        j.status === 'processing' &&
                                        j.total_emails > 0 &&
                                        j.processed_emails < j.total_emails
                                    ).length} / {workerConcurrency}
                                </span>
                                <span className="text-gray-500">processing jobs</span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-500">
                                    {jobs.filter(j => j.status === 'pending').length} pending
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="card-body">
                    {jobsLoading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 py-6">
                            {[...Array(jobsPerPage)].map((_, idx) => (
                                <div key={idx} className="relative bg-white dark:bg-gray-900 rounded-xl shadow p-3 flex flex-col justify-between border border-gray-200 dark:border-gray-800 animate-pulse">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-2">
                                            <div className="h-4 w-4 bg-gray-200 dark:bg-gray-800 rounded" />
                                            <div className="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded" />
                                        </div>
                                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-800 rounded" />
                                    </div>
                                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-800 rounded mb-4" />
                                    <div className="h-3 w-full bg-gray-200 dark:bg-gray-800 rounded-full mb-4" />
                                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                    </div>
                                    <div className="flex space-x-2 mt-auto">
                                        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-800 rounded" />
                                        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-800 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : displayJobs.length > 0 ? (
                        <>
                            <div className="max-w-2xl mx-auto mb-6">
                                <label className="block text-lg font-semibold text-blue-700 mb-2">Search or Select a List</label>
                                <Select
                                    classNamePrefix="react-select"
                                    options={jobs.map(job => ({ value: job.job_id, label: job.job_name || job.job_id }))}
                                    placeholder="Type to search by name or ID..."
                                    isClearable
                                    value={jobSearchTerm ? { value: jobSearchTerm, label: jobs.find(j => j.job_id === jobSearchTerm)?.job_name || jobSearchTerm } : null}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    styles={customSelectStyles}
                                    onChange={option => {
                                        if (option && option.value) {
                                            setJobSearchTerm(option.value);
                                            setJobPage(1); // Reset to page 1 when filtering
                                        } else {
                                            setJobSearchTerm('');
                                        }
                                    }}
                                />
                                {jobSearchTerm && (
                                    <button
                                        onClick={() => setJobSearchTerm('')}
                                        className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        &times; Clear Search Filter
                                    </button>
                                )}
                                <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-2 text-sm mt-4 flex items-center gap-2 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300">
                                    <InfoIcon className="h-4 w-4 text-blue-400" />
                                    Running and paused jobs are always shown at the top for your convenience.
                                </div>
                            </div>
                            <div style={{ marginTop: 8, marginBottom: 16, color: '#888', fontSize: 13 }}>
                                Note: Running jobs are shown first, then paused jobs, then others.
                            </div>
                            {/* Active Verifications Section - Show only if not on page 1 or if we want to highlight them */}
                            {jobs.some(j => j.status === 'processing' && j.processed_emails < j.total_emails) && (
                                <div className="mb-6 animate-fade-in">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="flex h-2 w-2 relative">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
                                        </div>
                                        <h4 className="text-sm font-bold text-brand-600 dark:text-brand-400 uppercase tracking-wider">Active Verifications</h4>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {jobs.filter(j => j.status === 'processing' && j.processed_emails < j.total_emails).map(job => (
                                            <div
                                                key={`active-${job.job_id}`}
                                                onClick={() => {
                                                    setSelectedJobToHighlight(job.job_id);
                                                }}
                                                className="glass border border-brand-200 dark:border-brand-900/50 px-4 py-2 rounded-xl flex items-center gap-3 cursor-pointer hover:bg-brand-50/50 dark:hover:bg-brand-900/10 transition-all group"
                                            >
                                                <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-slate-900 dark:text-white truncate max-w-[150px]">{job.job_name}</p>
                                                    <p className="text-[10px] text-slate-500 font-medium">Click to view details</p>
                                                </div>
                                                <div className="h-1.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex-shrink-0">
                                                    <div
                                                        className="h-full bg-brand-500 transition-all duration-1000"
                                                        style={{ width: `${Math.round((job.processed_emails / (job.total_emails || 1)) * 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {displayJobs.map((job) => (
                                    <div key={job.job_id} id={`job-card-${job.job_id}`} className={`relative bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-5 flex flex-col justify-between border border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300 ${job.status === 'processing' && job.processed_emails < job.total_emails ? 'ring-2 ring-brand-500 shadow-brand-100 dark:shadow-brand-900/20' : ''} ${job.status === 'deleting' ? 'opacity-60 grayscale pointer-events-none' : ''}`}>
                                        {/* Deleting state */}
                                        {job.status === 'deleting' && (
                                            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-white dark:bg-gray-900 bg-opacity-80 rounded-xl">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                                    <span className="font-semibold text-gray-500">Deleting...</span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-2">
                                            <div className="flex items-center space-x-2 min-w-0 max-w-full">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedJobs.has(job.job_id)}
                                                    onChange={() => handleSelectJob(job.job_id)}
                                                    disabled={isDeletingAny}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <span
                                                    className="font-bold text-slate-900 dark:text-white truncate text-base block mb-1 flex-1"
                                                    data-tip={job.job_name}
                                                    data-for={`job-name-tooltip-${job.job_id}`}
                                                >
                                                    {job.job_name}
                                                </span>
                                                <ReactTooltip
                                                    id={`job-name-tooltip-${job.job_id}`}
                                                    place="top"
                                                    effect="solid"
                                                    className="!max-w-xs !text-xs !rounded !shadow-lg !bg-gray-900 !text-white"
                                                />
                                            </div>
                                            {/* Hide status badge for uploading jobs */}
                                            {!(job.status === 'uploading' || job.status === 'uploading_paused') && (
                                                <span className={`ml-2 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${(job.status === 'completed' || (job.status === 'processing' && job.processed_emails >= job.total_emails && job.total_emails > 0)) ? 'bg-green-100 text-green-700' :
                                                    job.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                                        job.status === 'ready' ? 'bg-yellow-100 text-yellow-700' :
                                                            job.status === 'paused' ? 'bg-orange-100 text-orange-700' :
                                                                job.status === 'failed' ? 'bg-red-100 text-red-700' :
                                                                    'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {getStatusText(job)}
                                                </span>
                                            )}

                                            {/* Verification percentage badge - show at top for processing jobs */}
                                            {job.status === 'processing' && getJobStats(job).total > 0 && (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                                                    {getJobStats(job).processed < getJobStats(job).total && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                                                    {Math.round((Math.min(getJobStats(job).processed, getJobStats(job).total) / getJobStats(job).total) * 100)}%
                                                    {getJobStats(job).processed < getJobStats(job).total ? ' verifying' : ' verified'}
                                                </span>
                                            )}

                                            {/* Completion percentage badge for completed jobs */}
                                            {job.status === 'completed' && (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                                    100% complete
                                                </span>
                                            )}

                                            {/* Progress/Uploading badge */}
                                            {(job.upload_status === 'processing') ? (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                    {job.total_emails > 0 ? `${Math.round((job.processed_emails / job.total_emails) * 100)}% ` : ''}
                                                    Uploading...
                                                </span>
                                            ) : job.upload_status === 'pending' ? (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200 flex items-center gap-1">
                                                    <Pause className="h-3 w-3 mr-1" />
                                                    Queued
                                                </span>
                                            ) : (
                                                shouldShow100PercentDone(job) && job.status !== 'processing' && job.status !== 'completed' && (
                                                    <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                                        100% done
                                                    </span>
                                                )
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-300 mb-1">Created: {new Date(job.created_at).toLocaleString()}</div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="text-[11px] font-bold text-gray-400 bg-gray-100 dark:bg-gray-800 dark:text-gray-500 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-800 font-mono select-all shrink-0" title="Job ID (click to select)">ID: {job.job_id}</span>
                                        </div>
                                        {/* Progress bar or uploading animation */}
                                        {shouldShowProgress(job) && (
                                            <div className="mb-4">
                                                <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-3">
                                                    <div
                                                        className={`h-3 rounded-full transition-all duration-500 ${job.status === 'paused' ? 'bg-orange-500' :
                                                            job.status === 'processing' ? 'bg-blue-500 animate-pulse' : 'bg-yellow-500'
                                                            }`}
                                                        style={{ width: `${Math.min(100, Math.round((getJobStats(job).processed / (getJobStats(job).total || 1)) * 100))}%` }}
                                                    ></div>
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-300 mt-2 text-right">
                                                    {job.status === 'processing' ? (
                                                        <>
                                                            {Math.min(getJobStats(job).processed, getJobStats(job).total)} / {getJobStats(job).total} verified
                                                            {getJobStats(job).processed < getJobStats(job).total && (
                                                                <span className="ml-2 inline-flex items-center">
                                                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                    Verifying...
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : job.status === 'completed' ? (
                                                        <>
                                                            {getJobStats(job).total} / {getJobStats(job).total} verified
                                                        </>
                                                    ) : job.status === 'ready' || job.status === 'pending' ? (
                                                        <>
                                                            {getJobStats(job).total} emails ready
                                                        </>
                                                    ) : (
                                                        <>
                                                            {Math.min(getJobStats(job).processed, getJobStats(job).total)} / {getJobStats(job).total} processed
                                                            {job.status === 'paused' && ' (Paused)'}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {job.upload_status === 'pending' && (
                                            <div className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                                <Pause className="h-5 w-5" />
                                                <span className="font-medium">Waiting for upload slot...</span>
                                            </div>
                                        )}
                                        {job.upload_status === 'processing' && (
                                            <div className="mb-4 flex items-center gap-2 text-blue-600 animate-pulse">
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                <span className="font-medium">Uploading and processing CSV... Please wait</span>
                                            </div>
                                        )}
                                        {/* Results summary for completed jobs and all jobs */}
                                        <div className="mb-6">
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                                <div className="flex justify-between items-center p-2 rounded bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                                    <span className="text-slate-500 dark:text-slate-400 font-medium text-xs">Total:</span>
                                                    <span className="font-bold text-slate-900 dark:text-white">{job.total_emails}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 rounded bg-green-50/50 dark:bg-green-900/10 border border-green-100/50 dark:border-green-900/20">
                                                    <span className="text-green-600 dark:text-green-400 font-medium text-xs">Valid:</span>
                                                    <span className="font-bold text-green-700 dark:text-green-300">{getJobStats(job).valid}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 rounded bg-red-50/50 dark:bg-red-900/10 border border-red-100/50 dark:border-red-900/20">
                                                    <span className="text-red-600 dark:text-red-400 font-medium text-xs">Invalid:</span>
                                                    <span className="font-bold text-red-700 dark:text-red-300">{getJobStats(job).invalid}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 rounded bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100/50 dark:border-amber-900/20" title="Catch-all emails are considered risky because the server accepts everything, making it impossible to verify the exact mailbox.">
                                                    <span className="text-amber-600 dark:text-amber-400 font-medium text-xs text-nowrap">Catch-all/Risky:</span>
                                                    <span className="font-bold text-amber-700 dark:text-amber-300">{getJobStats(job).catch_all}</span>
                                                </div>

                                                {/* Mini Stats Grid */}
                                                <div className="col-span-2 mt-1 grid grid-cols-3 gap-2">
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Unknown</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).unknown}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Syntax</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).syntax_error}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Disposable</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).disposable}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Free Acc</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).free_account}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Role Acc</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).role_account}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Skipped</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).skipped}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Action buttons, only show if not uploading */}
                                        <div className="flex flex-wrap gap-2 mt-auto">
                                            {/* Uploading/Paused: Only show Cancel
                      {job.status === 'deleting' ? null : (job.status === 'uploading' || job.status === 'uploading_paused') && (
                        <button
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            (deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                          }`}
                          onClick={() => handleDeleteJob(job.job_id)}
                          disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                          title={'Cancel upload'}
                        >
                          {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                              Cancelling...
                            </>
                          ) : (
                            'Cancel'
                          )}
                        </button>
                      )} */}
                                            {/* Pending/Ready: Start and Delete */}
                                            {job.status === 'deleting' ? null : ['pending', 'ready'].includes(job.status) && (
                                                <>
                                                    <button
                                                        onClick={() => handleStartJob(job.job_id)}
                                                        disabled={!canStartNewJob()}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${!canStartNewJob()
                                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                                                            }`}
                                                        title={canStartNewJob() ? 'Start verification' : `Concurrency limit reached (${workerConcurrency} jobs max)`}
                                                    >
                                                        <Play className="h-4 w-4 mr-1" />
                                                        Start
                                                    </button>
                                                    <button
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                            }`}
                                                        onClick={() => handleDeleteJob(job.job_id)}
                                                        disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                        title={'Delete job'}
                                                    >
                                                        {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete'
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                            {/* Processing: Cancel, Pause and Results */}
                                            {job.status === 'deleting' ? null : job.status === 'processing' && (
                                                <>
                                                    {getJobStats(job).processed >= getJobStats(job).total && getJobStats(job).total > 0 && (
                                                        <button
                                                            onClick={() => navigate('/results', { state: { jobId: job.job_id } })}
                                                            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                            title={'View results'}
                                                        >
                                                            <FileText className="h-4 w-4 mr-1" />
                                                            View Results
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleCancelJobById(job.job_id)}
                                                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                        title={'Cancel verification'}
                                                    >
                                                        Cancel
                                                    </button>
                                                    {getJobStats(job).processed < getJobStats(job).total && (
                                                        <button
                                                            onClick={() => handlePauseJob(job.job_id)}
                                                            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-orange-600 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                                                            title={'Pause job'}
                                                        >
                                                            <Pause className="h-4 w-4 mr-1" />
                                                            Pause
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                            {/* Paused: Resume and Cancel */}
                                            {job.status === 'deleting' ? null : job.status === 'paused' && (
                                                <>
                                                    <button
                                                        onClick={() => handleResumeJob(job.job_id)}
                                                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                                        title={'Resume job'}
                                                    >
                                                        <Play className="h-4 w-4 mr-1" />
                                                        Resume
                                                    </button>
                                                    <button
                                                        onClick={() => handleCancelJobById(job.job_id)}
                                                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                        title={'Cancel verification'}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            )}
                                            {/* Completed: Download and Delete */}
                                            {job.status === 'deleting' ? null : job.status === 'completed' && (
                                                <>
                                                    <button
                                                        onClick={() => navigate('/results', { state: { jobId: job.job_id } })}
                                                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                        title={'View results'}
                                                    >
                                                        <FileText className="h-4 w-4 mr-1" />
                                                        View Results
                                                    </button>
                                                    <button
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                            }`}
                                                        onClick={() => handleDeleteJob(job.job_id)}
                                                        disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                        title={'Delete job'}
                                                    >
                                                        {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete'
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                            {/* Cancelled: Show Delete button */}
                                            {job.status === 'deleting' ? null : job.status === 'cancelled' && (
                                                <button
                                                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                        : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                        }`}
                                                    onClick={() => handleDeleteJob(job.job_id)}
                                                    disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                    title={'Delete job'}
                                                >
                                                    {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                            Deleting...
                                                        </>
                                                    ) : (
                                                        'Delete'
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 px-2">
                                <SmartPagination
                                    showPageSize={false}
                                    currentPage={Math.min(jobPage, totalJobPages)}
                                    totalPages={totalJobPages}
                                    totalRows={jobsTotal}
                                    pageSize={jobsPerPage}
                                    onPageChange={setJobPage}
                                    disabled={jobsLoading}
                                    className="!pt-0 !border-t-0"
                                />
                            </div>
                        </>
                    ) : (
                        <div className="text-center text-gray-500 py-12">
                            <span className="text-lg">No jobs found.</span>
                            {jobSearchTerm && (
                                <div className="mt-4">
                                    <button
                                        onClick={() => setJobSearchTerm('')}
                                        className="text-blue-600 hover:text-blue-800 font-semibold"
                                    >
                                        Clear search filter to see all jobs
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {
                (bulkResults || (previewItems.length > 0 && previewJobId)) && (
                    <div className="card mt-6">
                        <div className="card-header flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    {bulkResults ? 'Verification Results' : 'Live Results Preview'}
                                </h3>
                                {!bulkResults && <p className="text-xs text-gray-500">Showing last 10 verified emails</p>}
                            </div>
                            {previewJobId && !bulkResults && (
                                <button
                                    onClick={() => navigate('/results', { state: { jobId: previewJobId } })}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    View All Results
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            {(bulkResults?.results?.length > 0 || previewItems.length > 0) ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead>
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">email</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">status</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">type</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">node</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(bulkResults?.results || previewItems).map((row, idx) => {
                                                return (
                                                    <tr key={idx} className="hover:bg-gray-50 border-t border-gray-100">
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm">{row.email}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap">
                                                            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${getStatusColor(row.status)}`}>
                                                                {getStatusIcon(row.status)}
                                                                {row.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">{row.type || 'Standard'}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">{row.satellite_node_name || (row.is_satellite ? 'Satellite' : 'Master')}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center text-gray-500 py-10">No results to display yet.</div>
                            )}
                        </div>
                    </div>
                )
            }
            {
                bulkResults && (
                    <div className="card">
                        <div className="card-header">
                            <h3 className="text-lg font-medium text-gray-900">Export Results</h3>
                        </div>
                        <div className="card-body">
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => exportResults('csv')}
                                    className="btn btn-secondary"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export as CSV
                                </button>
                                <button
                                    onClick={() => exportResults('json')}
                                    className="btn btn-secondary"
                                >
                                    <FileText className="h-4 w-4 mr-2" />
                                    Export as JSON
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Delete Job Confirmation Modal */}
            {
                deleteModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-900 dark:border dark:border-gray-800 rounded-lg p-6 max-w-md mx-4">
                            <div className="flex items-center mb-4">
                                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                                <h3 className="text-lg font-medium text-gray-900">Delete Job</h3>
                            </div>
                            <p className="text-gray-600 mb-6">
                                Are you sure you want to delete this verification job? This action cannot be undone and will permanently remove all associated data.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setDeleteModalOpen(false);
                                        setJobToDelete(null);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                    No, Keep Job
                                </button>
                                <button
                                    onClick={confirmDeleteJob}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    Yes, Delete Job
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Cancel Job Confirmation Modal */}
            {
                cancelModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-900 dark:border dark:border-gray-800 rounded-lg p-6 max-w-md mx-4">
                            <div className="flex items-center mb-4">
                                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                                <h3 className="text-lg font-medium text-gray-900">Cancel Job</h3>
                            </div>
                            <p className="text-gray-600 mb-6">
                                Are you sure you want to cancel this verification job? This action cannot be undone.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        if (isCancellingJob) return;
                                        setCancelModalOpen(false);
                                        setJobToCancel(null);
                                    }}
                                    className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${isCancellingJob ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    disabled={isCancellingJob}
                                >
                                    No, Keep Running
                                </button>
                                <button
                                    onClick={confirmCancelJob}
                                    className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${isCancellingJob ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                                    disabled={isCancellingJob}
                                >
                                    {isCancellingJob ? (<span className="inline-flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cancelling...</span>) : 'Yes, Cancel Job'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* How It Works Section */}
            <div className="card mt-8">
                <div className="card-header">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <InfoIcon className="h-6 w-6 text-brand-500" />
                        How It Works
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Our multi-layer verification process ensures maximum accuracy
                    </p>
                </div>
                <div className="card-body">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-brand-100 dark:bg-brand-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Syntax Validation</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Checks for valid email format and structure</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">DNS MX Record Check</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Ensures the domain can receive mail</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-warning-100 dark:bg-warning-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Disposable Email Detection</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Flags temporary/throwaway addresses</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Mail className="h-5 w-5 text-success-600 dark:text-success-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">SMTP Handshake Verification</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Attempts to connect to the mail server</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <InfoIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Catch-All Domain Detection</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Identifies domains that accept all mail</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-brand-50 to-primary-50 dark:from-brand-900/20 dark:to-primary-900/20 rounded-xl border-2 border-brand-200 dark:border-brand-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-gradient-to-br from-brand-500 to-primary-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                                <span className="text-white text-xl">✨</span>
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Advanced Magic Logic</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Proprietary algorithms for even smarter verification 😃</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}